import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Firebase } from '../../firebase/config';
import PostCards from '../PostCards/PostCards';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import { CardSkeleton } from '../UI/Skeleton';
import { CATEGORIES, getSubcategories } from '../../data/categories';
import './CategoryPage.css';

function CategoryPage() {
  const { categoryId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subcategory, setSubcategory] = useState('');

  const category = CATEGORIES.find(
    (c) =>
      c.id === categoryId ||
      c.name.toLowerCase().replace(/\s+/g, '-') === categoryId
  );
  const categoryName = category?.name || categoryId;
  const subcategories =
    category?.subcategories || getSubcategories(categoryName) || [];

  useEffect(() => {
    setLoading(true);
    Firebase.firestore()
      .collection('products')
      .where('category', '==', categoryName)
      .where('status', '==', 'active')
      .orderBy('createdAt', 'desc')
      .get()
      .then((snapshot) => {
        let list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        if (subcategory) {
          list = list.filter(
            (p) => (p.subcategory || '').trim() === subcategory
          );
        }
        setProducts(list);
      })
      .finally(() => setLoading(false));
  }, [categoryName, subcategory]);

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: categoryName, to: `/category/${categoryId}` },
    ...(subcategory ? [{ label: subcategory }] : []),
  ].filter((item) => item.label);

  if (!category && !categoryId) {
    return null;
  }

  const catTitle = `${categoryName} - Buy & Sell ${categoryName} in Nigeria | DealSpotNG Classifieds`;
  const catDescription = `Browse ${categoryName} classified ads and listings on DealSpotNG. Find the best deals on ${categoryName} in Nigeria. Post free ads and buy or sell ${categoryName} online.`;

  return (
    <div className="categoryPageWrap">
      <Helmet>
        <title>{catTitle}</title>
        <meta name="description" content={catDescription} />
        <meta
          name="keywords"
          content={`${categoryName}, buy ${categoryName}, sell ${categoryName}, ${categoryName} Nigeria, ${categoryName} classifieds, ${categoryName} listings, DealSpotNG, marketplace, deals`}
        />
        <link
          rel="canonical"
          href={`https://dealspotng.dabaras.com/category/${categoryId}`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={catTitle} />
        <meta property="og:description" content={catDescription} />
        <meta
          property="og:url"
          content={`https://dealspotng.dabaras.com/category/${categoryId}`}
        />
        <meta property="og:site_name" content="DealSpotNG" />
        <meta
          property="og:image"
          content="https://dealspotng.dabaras.com/assets/images/dealspotngicon.png"
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={catTitle} />
        <meta name="twitter:description" content={catDescription} />
      </Helmet>
      <Breadcrumbs items={breadcrumbItems} />
      <h1 className="categoryPageTitle">{categoryName}</h1>
      {subcategories.length > 0 && (
        <div className="categoryPageTabs">
          <button
            type="button"
            className={`categoryPageTab ${!subcategory ? 'active' : ''}`}
            onClick={() => setSubcategory('')}
          >
            All
          </button>
          {subcategories.map((sub) => (
            <button
              type="button"
              key={sub}
              className={`categoryPageTab ${subcategory === sub ? 'active' : ''}`}
              onClick={() => setSubcategory(sub)}
            >
              {sub}
            </button>
          ))}
        </div>
      )}
      <div
        className={`categoryPageGrid ${!loading && products.length > 0 ? 'DealSpotNGFadeIn' : ''}`}
      >
        {loading ? (
          [1, 2, 3, 4, 5, 6, 7, 8].map((i) => <CardSkeleton key={i} />)
        ) : products.length === 0 ? (
          <p className="categoryPageEmpty">No ads in this category yet.</p>
        ) : (
          products.map((product, index) => (
            <div key={product.id || index} className="categoryPageCard">
              <PostCards product={product} index={index} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CategoryPage;
